import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import type { TrackEventFunction } from '@/hooks/useTrackEvents/types';
import type { Sort } from '@/utils/types';

import { enhanceColumns } from './utils';

type Props<T> = {
  columns?: ColumnsType<T>;
  sort?: Sort;
  onSort?: (value: Sort | undefined) => void;
  setSort: (value: Sort | undefined) => void;
  hiddenColumns?: string[];
  /**
   * Owning page's tracker. Header sorting is the only sort affordance on a
   * table that hides the toolbar Sort button, so it reports the same events
   * that button does rather than leaving those interactions unmeasured.
   */
  trackEvent?: TrackEventFunction;
};

const useSortableColumns = <T extends object>({
  columns,
  sort,
  onSort,
  setSort,
  hiddenColumns,
  trackEvent
}: Props<T>) => {
  const handleSort = useCallback(
    (sortBy: string) => {
      const onSortChange = onSort ?? setSort;

      if (!onSortChange) {
        return;
      }

      if (sort?.sort_by !== sortBy) {
        trackEvent?.('Click to apply sort', {
          column: sortBy,
          order: 'asc'
        });

        return onSortChange({
          sort_by: sortBy,
          sort_order: 'asc'
        });
      }

      // Third click on the same header drops the sort instead of cycling back.
      const isClearing = sort.sort_order === 'desc';

      if (isClearing) {
        trackEvent?.('Click to clear sort', { column: sortBy });
      } else {
        trackEvent?.('Click to apply sort', { column: sortBy, order: 'desc' });
      }

      onSortChange(
        isClearing ? undefined : { sort_by: sortBy, sort_order: 'desc' }
      );
    },
    [onSort, setSort, sort, trackEvent]
  );

  const enhancedColumns = useMemo(() => {
    if (!columns) return columns;

    return enhanceColumns({ columns, sort, hiddenColumns, handleSort });
  }, [sort, handleSort, columns, hiddenColumns]);

  return { enhancedColumns };
};

export default useSortableColumns;
